import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ListChecks, Folder, UsersThree, Briefcase, Plus, X } from "@phosphor-icons/react";

/**
 * TriforceCreate — floating quick-create FAB.
 *
 * Collapsed: a single gold circular button in the bottom-right corner.
 * Expanded: fans out UP-LEFT of the FAB into a Zelda-inspired Triforce
 * arrangement of four create shortcuts — Task ▲ top, Meetup ▲ bottom-left,
 * Job ▲ bottom-right, Project ▽ inverted-centre. Auto-collapses after
 * `IDLE_MS` of no hover, or when the ✕ minimize badge is tapped.
 */

const IDLE_MS = 4500;
const T = 74;       // solid triangle size (px)
const M = 60;       // centre inverted triangle size (px)

// Positions are the *centre* of each tile in the coordinate space of
// the FAB button's centre (bottom-right corner of the screen).
// Everything fans up-left so nothing clips the viewport edge.
const ACTIONS = [
  { key: "task",    label: "Task",    icon: ListChecks, path: "/app/tasks?new=1",    pos: { cx: -90,  cy: -230 }, inverted: false, size: T },
  { key: "meetup",  label: "Meetup",  icon: UsersThree, path: "/app/meetups?new=1",  pos: { cx: -145, cy: -125 }, inverted: false, size: T },
  { key: "job",     label: "Job",     icon: Briefcase,  path: "/app/jobs?new=1",     pos: { cx: -35,  cy: -125 }, inverted: false, size: T },
  { key: "project", label: "Project", icon: Folder,     path: "/app/projects?new=1", pos: { cx: -90,  cy: -135 }, inverted: true,  size: M },
];

export default function TriforceCreate() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const idleRef = useRef(null);

  const armIdle = () => {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => setOpen(false), IDLE_MS);
  };
  const cancelIdle = () => {
    if (idleRef.current) { clearTimeout(idleRef.current); idleRef.current = null; }
  };
  useEffect(() => {
    if (open) armIdle(); else cancelIdle();
    return cancelIdle;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div
      className="fixed z-[70] select-none"
      style={{ right: 26, bottom: 26 }}
      onMouseEnter={cancelIdle}
      onMouseLeave={armIdle}
      data-testid="triforce-fab"
    >
      {/* Radial glow behind the expanded panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="glow"
            initial={{ opacity: 0, scale: 0.55 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.55 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute pointer-events-none"
            style={{
              width: 340, height: 340,
              left: -260, top: -280,
              background: "radial-gradient(circle at 55% 60%, rgba(255,215,0,0.28) 0%, rgba(212,175,55,0.09) 42%, transparent 72%)",
              filter: "blur(10px)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Triforce triangles fanning up-left */}
      <AnimatePresence>
        {open && ACTIONS.map((a, i) => (
          <motion.button
            key={a.key}
            onClick={() => go(a.path)}
            data-testid={`triforce-${a.key}`}
            initial={{ opacity: 0, x: 0, y: 0, scale: 0.35, rotate: -18 }}
            animate={{ opacity: 1, x: a.pos.cx, y: a.pos.cy, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, x: 0, y: 0, scale: 0.35, rotate: 12, transition: { duration: 0.22 } }}
            transition={{ duration: 0.55, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            className="absolute cursor-pointer"
            aria-label={`Create ${a.label}`}
            // Centre the tile on (cx, cy). marginLeft/Top take priority when
            // motion applies the transform, so the anchor stays predictable.
            style={{
              left: 28, top: 28,   // FAB centre
              width: a.size, height: a.size,
              marginLeft: -a.size / 2,
              marginTop: -a.size / 2,
              background: "transparent",
              border: "none",
              padding: 0,
              overflow: "visible",
            }}
          >
            <TriangleTile action={a}/>
          </motion.button>
        ))}
      </AnimatePresence>

      {/* Minimize badge — floats above the FAB when the panel is open */}
      <AnimatePresence>
        {open && (
          <motion.button
            key="min"
            onClick={() => setOpen(false)}
            data-testid="triforce-minimize"
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.4, transition: { duration: 0.18 } }}
            transition={{ duration: 0.32, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute w-8 h-8 rounded-full flex items-center justify-center"
            style={{
              left: -12, top: -34,
              background: "rgba(5,3,0,0.94)",
              border: "1px solid rgba(212,175,55,0.65)",
              boxShadow: "0 4px 14px rgba(0,0,0,0.6)",
            }}
            aria-label="Minimize quick create"
          >
            <X size={12} weight="bold" color="#F2DC8C"/>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Main FAB */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="triforce-toggle"
        aria-label={open ? "Close quick create" : "Open quick create"}
        whileTap={{ scale: 0.92 }}
        animate={{ rotate: open ? 135 : 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className="relative w-14 h-14 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #6b4d00 100%)",
          border: "1px solid rgba(255,243,174,0.9)",
          boxShadow: open
            ? "0 0 22px rgba(255,215,0,0.55), 0 8px 22px rgba(0,0,0,0.55)"
            : "0 6px 18px rgba(0,0,0,0.55), 0 0 10px rgba(255,215,0,0.25)",
        }}
      >
        <Plus size={22} weight="bold" color="#0a0703"/>
      </motion.button>
    </div>
  );
}

/**
 * TriangleTile — a triforce-slot shaped button. `inverted` renders the
 * center piece as a downward-pointing (▽) triangle. Label is rendered
 * *outside* the clip-path so long words (e.g. "PROJECT") aren't chopped
 * by the narrow apex — it simply extends past the triangle's silhouette.
 */
function TriangleTile({ action }) {
  const Icon = action.icon;
  const size = action.size;
  const clip = action.inverted
    ? "polygon(50% 100%, 100% 0%, 0% 0%)"
    : "polygon(50% 0%, 100% 100%, 0% 100%)";
  return (
    <div className="relative" style={{ width: size, height: size, overflow: "visible" }}>
      {/* Gold triangle outline */}
      <div
        className="absolute inset-0"
        style={{
          clipPath: clip,
          background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
          filter: "drop-shadow(0 4px 12px rgba(255,215,0,0.35))",
        }}
      />
      {/* Dark inner plate */}
      <div
        className="absolute inset-[3px]"
        style={{
          clipPath: clip,
          background: "linear-gradient(180deg, rgba(26,18,6,0.94) 0%, rgba(5,3,0,0.98) 100%)",
        }}
      />
      {/* Icon — inside the triangle */}
      <div
        className="absolute inset-0 flex items-start justify-center pointer-events-none"
        style={{ paddingTop: action.inverted ? 10 : Math.round(size * 0.34) }}
      >
        <Icon size={16} weight="bold" color="#FFD700"/>
      </div>
      {/* Label — floats over the triangle, can bleed past its edges */}
      <div
        className="absolute left-1/2 -translate-x-1/2 text-[8px] uppercase leading-none whitespace-nowrap pointer-events-none"
        style={{
          top: action.inverted ? size * 0.42 : size * 0.68,
          letterSpacing: "0.18em",
          color: "#F2DC8C",
          fontFamily: "'Cormorant Garamond', serif",
          textShadow: "0 1px 4px rgba(0,0,0,0.9)",
        }}
      >
        {action.label}
      </div>
    </div>
  );
}
